<template>
    <div v-if="!continueJumping" class="container mx-auto mt-6 py-12 px-4 sm:px-6 lg:px-8">

        <button @click="goBack" class="block mb-2">
            <i class="fa fa-arrow-left mr-2">返回</i>
        </button>

        <!-- 书本信息卡片 -->
        <div class="hidden md:block bg-white rounded-lg shadow-md mb-8 p-6">
            <div class="flex flex-col md:flex-row items-center justify-between">
                <!-- 左侧：封面 + 基础信息 -->
                <div v-if="NovelData" :key="NovelData.id" class="flex items-center space-x-6 md:space-x-10">
                    <!-- 书本封面 -->
                    <div class="w-32 h-48 md:w-40 md:h-60 bg-cover bg-no-repeat rounded-lg"
                        :style="{ backgroundImage: `url(${NovelData.coverImage})` }"></div>

                    <!-- 书本详情 -->
                    <div class="flex-1">
                        <h1 class="text-2xl font-bold text-primary mb-2">{{ NovelData.title }}</h1>
                        <div class="text-gray-600 mb-4">
                            <span class="mr-4"><i class="fa fa-tag text-secondary"></i> {{ NovelData.category }}</span>
                            <span><i class="fa fa-eye text-gray-500"></i> {{ NovelData.views }}次阅读</span>
                        </div>
                        <p class="text-sm text-gray-700">{{ NovelData.description }}</p>
                    </div>
                </div>

            </div>
        </div>
        <!-- 移动端展示 -->
        <div
            class="block md:hidden w-full bg-white rounded-xl shadow-sm mb-4 overflow-hidden transition-all duration-200 hover:shadow-md">
            <!-- 封面与内容容器 -->
            <div class="flex">
                <!-- 封面图片 - 增加圆角和阴影效果 -->
                <div class="w-24 h-32 flex-shrink-0 relative overflow-hidden rounded-l-xl">
                    <img :src="NovelData?.coverImage" alt="书籍封面"
                        class="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        onerror="this.src='placeholder-book-cover.jpg'">
                    <!-- 封面装饰元素 -->
                    <div class="absolute inset-0 border-r border-gray-100/50"></div>
                </div>

                <!-- 内容区域 -->
                <div class="p-3 flex-1 flex flex-col">
                    <!-- 标题 - 增加行数限制和更好的字体层次 -->
                    <h1 class="text-lg font-bold text-gray-800 mb-1 line-clamp-2 leading-tight">
                        {{ NovelData?.title }}
                    </h1>

                    <!-- 元信息 - 更精致的图标和排版 -->
                    <div class="flex items-center text-xs text-gray-500 mb-2 space-x-3">
                        <span class="flex items-center">
                            <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                            {{ NovelData?.rating || '暂无评分' }}
                        </span>
                        <span class="flex items-center">
                            <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                                    clip-rule="evenodd" />
                            </svg>作者：
                            {{ NovelData?.author || '未知作者' }}
                        </span>
                    </div>

                    <!-- 简介 - 更好的可读性和交互 -->
                    <div class="text-xs text-gray-600 mb-2 flex-1">
                        <details class="group">
                            <summary class="flex items-center cursor-pointer list-none">
                                <!-- 简介内容 - 未展开时显示3行 -->
                                <span class="line-clamp-3 leading-relaxed">
                                    {{ NovelData?.description }}
                                </span>

                                <!-- "更多"按钮 -->
                                <span class="text-primary inline " @click="toggleDetails">
                                    更多
                                </span>
                            </summary>
                            <!-- 滑动显示的完整简介面板 -->

                        </details>
                    </div>


                    <!-- 操作按钮 -->
                    <div class="flex justify-between items-center pt-1">
                        <span class="text-xs font-medium text-primary">
                            {{ NovelData?.type || '未分类' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>


        <!-- 章节列表 -->
        <div class="bg-white rounded-lg shadow-md p-6">
            <div class="flex items-center justify-between mb-6">
                <h2 class="text-xl font-medium">小说章节</h2>
                <button @click="toggleOrder"
                    class="flex items-center space-x-1 px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
                    <i :class="orderAsc ? 'fa fa-sort-amount-asc' : 'fa fa-sort-amount-desc'"></i>
                    <span>{{ orderAsc ? '正序' : '倒序' }}</span>
                </button>
            </div>
            <div class="space-y-4">
                <div v-for="chapter in sortedChapters" :key="chapter.id"
                    class="flex items-center justify-between hover:bg-neutral-50 p-3 rounded-lg cursor-pointer"
                    :class="{ 'bg-primary/5 border-l-4 border-primary': Number(chapter.id) === lastReadChapterId }"
                    @click="goToReader(chapter)">
                    <!-- 章节信息 -->
                    <div class="flex items-center space-x-2">
                        <span class="text-sm font-medium text-gray-800">{{ chapter.title }}</span>
                        <span class="text-xs text-gray-500">发布于 {{ chapter.createdAt.substring(0, 10) }}</span>
                        <span v-if="Number(chapter.id) === lastReadChapterId"
                              class="ml-2 px-2 py-0.5 text-xs rounded-full bg-primary text-white font-medium">
                            上次读到
                        </span>
                    </div>
                    <!-- 章节字数 -->
                    <span class="text-sm text-gray-600">{{ chapter.wordCount }}字</span>
                </div>
            </div>

            <!-- 分页导航（预留） -->
            <div class="mt-6 flex justify-end space-x-2">
                <button class="px-4 py-2 rounded-lg hover:bg-neutral-100">上一页</button>
                <button class="px-4 py-2 rounded-lg hover:bg-neutral-100">下一页</button>
            </div>
        </div>


        <div class="fixed top-0 right-0 bottom-0 w-72 bg-white shadow-xl transform translate-x-full transition-transform duration-300 z-20"
            :class="{ '-translate-x-0': isOpen }">
            <div class="p-4 border-b">
                <h3 class="font-medium">完整简介</h3>
                <button @click="toggleDetails" class="absolute top-4 right-4">
                    <i class="fa fa-times"></i>
                </button>
            </div>
            <div v-if="htmlContent" class="prose prose-lg prose-slate max-w-none text-reader-text"
                :style="{ 'font-size': `12px`, 'line-height': '1' }">
                <!--                HTML会自动忽略换行符和连续空格-->
                <div v-html="htmlContent" class="prose mt-3 max-w-none dark:prose-invert"></div>
            </div>


        </div>
        <div class="fixed inset-0 bg-black/30 z-10 opacity-0 pointer-events-none transition-opacity duration-300"
            :class="{ 'opacity-100 pointer-events-auto': isOpen }" @click="toggleDetails"></div>



    </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { chapterListWebSocket } from "@/services/ChapterWebSocketService";

import { useChapterStore } from "@/store/chapter";
import { useDisableDevTools } from "@/composables/useDisableDevTools";

const chapterStore = useChapterStore();

// 模拟数据（实际需从API获取）
const route = useRoute();
const novel_Id = route.params.id; // 直接获取参数

// 检测"继续阅读"跳转标记：带 jump 参数时先不渲染内容，立即续跳到阅读页
const continueJumping = ref(!!route.query.jump)

// 从 localStorage 读取该小说的上次阅读记录
const lastReadChapterId = ref(null)
const loadLastRead = () => {
    const saved = localStorage.getItem(`read_progress_${novel_Id}`)
    if (saved) {
        try {
            const progress = JSON.parse(saved)
            lastReadChapterId.value = Number(progress.chapterId)
        } catch (e) { /* ignore */ }
    }
}

const {
    connected,
    chapter_introduce,
    novels,
    loading,
    error,
    error2,
    fetchNovelsChapter
} = chapterListWebSocket(novel_Id);
// 排序相关状态
const orderAsc = ref(true);

// 排序后的章节列表
const sortedChapters = computed(() => {
    if (!chapter_introduce.value || !Array.isArray(chapter_introduce.value)) {
        return [];
    }
    const sorted = [...chapter_introduce.value];
    sorted.sort((a, b) => {
        return orderAsc.value ? a.id - b.id : b.id - a.id;
    });
    return sorted;
});

// 切换排序顺序
const toggleOrder = () => {
    orderAsc.value = !orderAsc.value;
};

// 组件挂载后自动请求数据（Axios 版本，无需等待 WebSocket 连接）
const loadData = async () => {
    await fetchNovelsChapter(novel_Id);
    console.log(chapter_introduce)
};


const NovelData = computed(() => {
    const defaultValue = {
        id: '0',
        novelId: '0',
        title: '加载中...',
        description: '加载中…',
        coverImage: '/cover_picture/loading.jpg',
        type: '……',
        updatedAt: '2005-01-25 00:00:00',
        author: '梦不沉沙',
    };

    // 合并数据
    return { ...defaultValue, ...(novels.value || {}) };
});
const escapeHTML = (str) => {
    return str
        .replace(/&/g, '&')
        .replace(/</g, '<')
        .replace(/>/g, '>')
        .replace(/"/g, '"')
        .replace(/'/g, '&#039;');
};
let htmlContent = computed(() => {
    return NovelData.value.description
        .split('\n')
        .map(line => `<p class="text-left mb-5 " style="text-indent: 2em">${escapeHTML(line)}</p>`)
        .join('');
});
// 切换展开/收起状态
const isOpen = ref(false);
const toggleDetails = () => {
    isOpen.value = !isOpen.value;
    console.log(isOpen.value)
};

// 导航函数
const goBack = () => {
    if (window.history.length > 1) {
        router.back()
    } else {
        router.push({name:'homepage'})
    }
}
// 路由跳转函数
const router = useRouter()
// 返回首页
const goToHome = () => {
    router.push('/') // 上传页面路由
}

// 进入阅读页
const goToReader = (chapter) => {
    chapterStore.setCurrentChapter(chapter.id, chapter.novelId);
    router.push({ name: 'NovelReader' });
}
// 生命周期钩子
onMounted(async () => {
    loadData()
    loadLastRead()
    // 若是"继续阅读"带 jump 标记进入，消费标记后自动跳到阅读页
    if (continueJumping.value) {
        // 还原 URL：替换当前条目去除 jump 参数（不新增历史条目）
        await router.replace({query: {}})
        continueJumping.value = false
        // 再进入阅读页
        router.push({name: 'NovelReader'})
    }
    // 禁用开发者工具快捷键
    const cleanup = useDisableDevTools()
    onUnmounted(cleanup)
})
onUnmounted(() => {

})
</script>
<style>
.back {}
</style>